import { Policy } from '@wsphere/warranties/domain';

/**
 * Returns the set of addon IDs that would introduce a circular dependency
 * if chosen as a prerequisite for `addonId`.
 *
 * This includes the addon itself (self-reference) plus any addon that
 * transitively depends on `addonId`.
 */
export function getInvalidDependencyTargets(addons: ReadonlyArray<Policy.Plan.Addon>, addonId: string): Set<string> {
  const invalid = new Set<string>();
  invalid.add(addonId); // self-reference

  // Find all addons that transitively depend on addonId (reverse reachability)
  // i.e., if B depends on A, then A cannot depend on B
  const reverseDeps = new Map<string, string[]>();
  for (const addon of addons) {
    for (const depId of addon.dependency?.requiredAddonIds ?? []) {
      if (!reverseDeps.has(depId)) reverseDeps.set(depId, []);
      (reverseDeps.get(depId) ?? []).push(addon.id);
    }
  }

  // DFS from addonId through reverse edges
  const stack = [addonId];
  while (stack.length) {
    const current = stack.pop();
    if (current === undefined) continue;
    for (const dependent of reverseDeps.get(current) ?? []) {
      if (!invalid.has(dependent)) {
        invalid.add(dependent);
        stack.push(dependent);
      }
    }
  }

  return invalid;
}

/**
 * Returns a human-readable label for the dependency of an addon.
 * e.g. "Requires all of: Gold Coverage, Silver Coverage"
 * e.g. "Requires any of: Basic, Premium"
 */
export function getDependencyLabel(addon: Policy.Plan.Addon, addonMap: Map<string, Policy.Plan.Addon>): string | null {
  if (!addon.dependency?.requiredAddonIds?.length) return null;

  const titles = addon.dependency.requiredAddonIds.map(id => addonMap.get(id)?.title ?? id).join(', ');

  return addon.dependency.operator === 'AND' ? `Requires all of: ${titles}` : `Requires any of: ${titles}`;
}

// ─── Buyer-facing dependency helpers ────────────────────────────────────────

export interface AddonDependencyStatus {
  addonId: string;
  dependenciesMet: boolean;
  requiredAddonTitles: string[];
  operator: Policy.Plan.AddonDependencyOperator;
}

/**
 * Client-side mirror of `AddonDependencyService.evaluateDependencyStatuses`.
 * For each addon, determines whether its prerequisite addons are currently selected.
 */
export function evaluateDependencyStatuses(
  addons: ReadonlyArray<Policy.Plan.Addon>,
  selectedAddonIds: ReadonlyArray<string>
): AddonDependencyStatus[] {
  const selected = new Set(selectedAddonIds);
  const addonMap = new Map(addons.map(a => [a.id, a]));

  return addons.map(addon => {
    const deps = addon.dependency?.requiredAddonIds ?? [];
    const operator = addon.dependency?.operator ?? 'AND';

    let dependenciesMet = true;
    if (deps.length > 0) {
      dependenciesMet = operator === 'AND' ? deps.every(id => selected.has(id)) : deps.some(id => selected.has(id));
    }

    return {
      addonId: addon.id,
      dependenciesMet,
      requiredAddonTitles: deps.map(id => addonMap.get(id)?.title ?? id),
      operator,
    };
  });
}

/**
 * Client-side mirror of `AddonDependencyService.validateSelection`.
 * Cascades removal: if deselecting addon A causes addon B's deps to be unmet,
 * B is also removed, which may cascade further.
 * Returns the stable set of selected addon IDs after cascade.
 */
export function cascadeDeselect(
  addons: ReadonlyArray<Policy.Plan.Addon>,
  selectedAddonIds: ReadonlyArray<string>
): string[] {
  const selected = new Set(selectedAddonIds);

  let changed = true;
  while (changed) {
    changed = false;
    for (const addonId of [...selected]) {
      const addon = addons.find(a => a.id === addonId);
      if (!addon?.dependency?.requiredAddonIds?.length) continue;

      const { operator, requiredAddonIds } = addon.dependency;
      const met =
        operator === 'AND'
          ? requiredAddonIds.every(id => selected.has(id))
          : requiredAddonIds.some(id => selected.has(id));

      if (!met) {
        selected.delete(addonId);
        changed = true;
      }
    }
  }

  return [...selected];
}
